import { useState, useCallback, useRef, useEffect } from 'react';
import {
  getChats,
  getChatMessages,
  getChatContext,
  updateChatCategory as updateChatCategoryService,
  deleteChat,
  updateChatTitle
} from '../../services/AGTChatService';
import { getProjects } from '../../../AGTHall/services/AGTHallService';

export function useChat(user, initialMessages = [], initialIsLoading = false) {
  // Estados principales del chat
  const [chats, setChats] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [messages, setMessages] = useState(initialMessages);
  const [visibleMessages, setVisibleMessages] = useState([]);
  const [chatContext, setChatContext] = useState({});
  const [chatTitle, setChatTitle] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [isNewChatView, setIsNewChatView] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // Estados de carga y error
  const [isLoading] = useState(initialIsLoading);
  const [loadingStates, setLoadingStates] = useState({
    messages: false,
    chatInfo: false,
  });
  const [error, setError] = useState(null);
  const [retryAction, setRetryAction] = useState(null);
  
  // Estados para la paginación
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [isNextPageLoading, setIsNextPageLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);
  
  // Estado para mensajes seleccionados de proyecto
  const [projectIdForBackend, setProjectIdForBackend] = useState(null);
  
  // Sistema de caché para chats
  const chatCache = useRef({
    byId: {},            // Datos del chat por ID
    recentIds: [],       // Orden de chats recientemente accedidos
    maxSize: 30,         // Máximo número de chats para cachear
    accessCount: {},     // Conteo de accesos para frecuencia
    totalAccesses: 0     // Conteo total de accesos para LFU (Least Frequently Used)
  });

  // Persistencia del estado del chat
  const [persistentChatState, setPersistentChatState] = useState(() => {
    try {
      const saved = localStorage.getItem('chatView_state');
      return saved
        ? JSON.parse(saved)
        : {
            lastScrollPosition: 0,
            lastVisibleCategory: null,
            loadedChatIds: [],
            totalChatsLoaded: 0,
          };
    } catch (error) {
      console.error('Error loading persistent chat state:', error);
      return {
        lastScrollPosition: 0,
        lastVisibleCategory: null,
        loadedChatIds: [],
        totalChatsLoaded: 0,
      };
    }
  });

  // Estados para categorías y filtros
  const [selectedCategory, setSelectedCategory] = useState(() => {
    const storedCategory = localStorage.getItem('selectedCategory');
    return storedCategory || 'personal';
  });
  const [customCategories, setCustomCategories] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [totalSearchResults, setTotalSearchResults] = useState(0);

  // Guardar categoría en localStorage cuando cambie
  useEffect(() => {
    localStorage.setItem('selectedCategory', selectedCategory);
  }, [selectedCategory]);

  // Guardar estado persistente
  useEffect(() => {
    try {
      localStorage.setItem('chatView_state', JSON.stringify(persistentChatState));
    } catch (error) {
      console.error('Error saving persistent chat state:', error);
    }
  }, [persistentChatState]);

  // Sistema de caché de chats
  const getCachedChat = useCallback((chatId) => {
    if (!chatCache.current.byId[chatId]) return null;
   
    // Actualizar estadísticas de acceso
    chatCache.current.accessCount[chatId] = 
      (chatCache.current.accessCount[chatId] || 0) + 1;
    chatCache.current.totalAccesses++;
   
    // Mover al frente de la lista reciente (comportamiento LRU)
    chatCache.current.recentIds = 
      [chatId, ...chatCache.current.recentIds.filter(id => id !== chatId)];
   
    return chatCache.current.byId[chatId];
  }, []);

  const setCachedChat = useCallback((chatId, messages, context) => {
    const cache = chatCache.current;
   
    // Estadísticas de acceso inicial si es nuevo
    if (!cache.byId[chatId]) {
      cache.accessCount[chatId] = 0;
      // Agregar al frente de la lista reciente
      cache.recentIds.unshift(chatId);
    } else {
      // Mover al frente si existe
      cache.recentIds = [chatId, ...cache.recentIds.filter(id => id !== chatId)];
    }
   
    // Almacenar datos
    cache.byId[chatId] = { 
      messages,
      context,
      timestamp: Date.now()
    };
   
    // Política de evicción - mantener tamaño del caché
    if (cache.recentIds.length > cache.maxSize) {
      // Encontrar primero los chats menos accedidos
      const sortedByAccess = [...cache.recentIds]
        .sort((a, b) => (cache.accessCount[a] || 0) - (cache.accessCount[b] || 0));
   
      // Eliminar la entrada más antigua y menos accedida
      const toRemove = sortedByAccess[0];
      cache.recentIds = cache.recentIds.filter(id => id !== toRemove);
      delete cache.byId[toRemove];
      delete cache.accessCount[toRemove];
    }
  }, []);

  // Guardar ID del chat actual
  const saveCurrentChatId = useCallback((chatId) => {
    if (chatId) {
      localStorage.setItem('currentChatId', chatId);
    } else {
      localStorage.removeItem('currentChatId');
    }
  }, []);

  // Ordenar chats por fecha más reciente
  const updateChatsOrder = useCallback(() => {
    setChats((prevChats) => {
      const sortedChats = [...prevChats].sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.createdAt);
        const dateB = new Date(b.updatedAt || b.createdAt);
        return dateB - dateA;
      });
      return sortedChats;
    });
  }, []);

  // Cargar lista de chats (opcional: cargar más con paginación)
  const loadChats = useCallback(
    async (loadMore = false) => {
      if (!user || !user.uid || (loadMore && isNextPageLoading)) return;

      setIsNextPageLoading(true);
      setError(null);

      try {
        // Opciones base
        const options = {
          limit: isSearchMode ? 50 : 20,
          lastEvaluatedKey: loadMore ? lastEvaluatedKey : null,
          category: selectedCategory !== 'add' ? selectedCategory : null
        };

        const response = await getChats(user.uid, options);

        if (!loadMore) {
          setChats(response.chats);
        } else {
          setChats(prevChats => {
            // Create a set of existing chat IDs for more efficient lookup
            const existingIds = new Set(prevChats.map(chat => chat.id));
            
            // Filter out chats that already exist
            const newChats = response.chats.filter(chat => !existingIds.has(chat.id));
            
            // Return combined list
            return [...prevChats, ...newChats];
          });
        }

        setHasNextPage(response.hasMore);
        setLastEvaluatedKey(response.lastEvaluatedKey);

        if (isSearchMode) {
          setTotalSearchResults(response.total || 0);
        }

      } catch (error) {
        console.error('Error loading chats:', error);
        setError('Error loading chats. Please try again.');
        setRetryAction(() => () => loadChats(loadMore));
      } finally {
        setIsNextPageLoading(false);
      }
    },
    [user, lastEvaluatedKey, isNextPageLoading, isSearchMode, selectedCategory]
  );

  // Seleccionar un chat existente
  const selectChat = useCallback(
    async (chatId) => {
      if (!user || !user.uid || chatId === currentChatId) return;
   
      const cachedSelectedChat = chats.find((c) => c.id === chatId);
      const isProjectChat = Boolean(cachedSelectedChat?.projectId || cachedSelectedChat?.type === 'project');
      if (!isProjectChat) {
        // Forzamos la vista normal (no ProjectChatView)
        setSelectedProject(null);
      }
   
      setIsFirstLoad(true);
      setMessages([]);
      setVisibleMessages([]);
   
      setCurrentChatId(chatId);
      saveCurrentChatId(chatId);
      setLoadingStates({ messages: true, chatInfo: true });
      setError(null);
      setIsNewChatView(false);
   
      try {
        let chatMessages, context;
        const cachedData = getCachedChat(chatId);
   
        if (cachedData) {
          chatMessages = cachedData.messages;
          context = cachedData.context;
        } else {
          [chatMessages, context] = await Promise.all([
            getChatMessages(user.uid, chatId),
            getChatContext(user.uid, chatId),
          ]);
          setCachedChat(chatId, chatMessages, context);
        }
   
        setMessages(chatMessages || []);
        setVisibleMessages((chatMessages || []).slice(-50));
        setChatContext(context || {});
   
        const selectedChat = chats.find((c) => c.id === chatId);
        if (selectedChat) {
          setChatTitle(selectedChat.title || 'New chat');

          if (selectedChat.projectId || selectedChat.type === 'project') {
            const projectId = selectedChat.projectId;
            if (projectId) {
              setProjectIdForBackend(projectId);
            }

            if (!selectedProject || selectedProject.id !== projectId) {
              let projectMatch = null;
              if (user?.uid) {
                const projects = await getProjects(user.uid);
                projectMatch = projects.find((project) => project.id === projectId) || null;
              }

              setSelectedProject({
                ...(projectMatch || {}),
                id: projectId,
                name: projectMatch?.name || selectedChat.projectName || selectedChat.projectTitle || selectedChat.title || 'Proyecto',
                category: projectMatch?.category || selectedChat.category || 'personal',
                currentView: 'chat'
              });
            } else if (selectedProject && selectedProject.currentView !== 'chat') {
              setSelectedProject((prev) => (prev ? { ...prev, currentView: 'chat' } : prev));
            }
          } else {
            setProjectIdForBackend(null);
          }
        } else {
          const response = await getChats(user.uid, { limit: 20 });
          const updatedSelectedChat = response.chats.find((c) => c.id === chatId);
          setChatTitle(
            updatedSelectedChat
              ? updatedSelectedChat.title || 'New chat'
              : 'New chat'
          );

          if (updatedSelectedChat?.projectId || updatedSelectedChat?.type === 'project') {
            const projectId = updatedSelectedChat.projectId;
            if (projectId) {
              setProjectIdForBackend(projectId);
            }

            if (!selectedProject || selectedProject.id !== projectId) {
              let projectMatch = null;
              if (user?.uid) {
                const projects = await getProjects(user.uid);
                projectMatch = projects.find((project) => project.id === projectId) || null;
              }
              setSelectedProject({
                ...(projectMatch || {}),
                id: projectId,
                name: projectMatch?.name || updatedSelectedChat.projectName || updatedSelectedChat.projectTitle || updatedSelectedChat.title || 'Proyecto',
                category: projectMatch?.category || updatedSelectedChat.category || 'personal',
                currentView: 'chat'
              });
            } else if (selectedProject && selectedProject.currentView !== 'chat') {
              setSelectedProject((prev) => (prev ? { ...prev, currentView: 'chat' } : prev));
            }
          } else {
            setProjectIdForBackend(null);
          }

          setChats(response.chats);
          updateChatsOrder();
        }
      } catch (error) {
        console.error('Error selecting chat:', error);
        setError('Failed to load chat data. Please try again.');
        setRetryAction(() => () => selectChat(chatId));
        setMessages([]);
        setVisibleMessages([]);
        setChatContext({});
        setIsFirstLoad(false);
      } finally {
        setLoadingStates({ messages: false, chatInfo: false });
        setIsFirstLoad(false);
      }
    },
    [
      user,
      chats,
      currentChatId,
      selectedProject,
      saveCurrentChatId,
      updateChatsOrder,
      getCachedChat,
      setCachedChat,
      setSelectedProject
    ]
  );

  // Cargar contexto del chat (solo)
  const loadChatContext = useCallback(
    async (chatId) => {
      if (!user || !user.uid) return;
      try {
        const context = await getChatContext(user.uid, chatId);
        setChatContext(context || {});
      } catch (error) {
        console.error('Error loading chat context:', error);
        setError('Error loading chat context. Some conversation history may be missing.');
        setRetryAction(() => () => loadChatContext(chatId));
      }
    },
    [user]
  );

  // Editar título de un chat
  const handleTitleEdit = useCallback(() => {
    setIsEditingTitle(true);
    setEditedTitle(chatTitle);
  }, [chatTitle]);

  const handleTitleSave = useCallback(async () => {
    if (!user || !user.uid || !currentChatId) return;
    try {
      const response = await updateChatTitle(user.uid, currentChatId, editedTitle);
      if (response && response.success) {
        setChatTitle(editedTitle);
        setChats((prevChats) =>
          prevChats.map((c) =>
            c.id === currentChatId
              ? {
                  ...c,
                  title: editedTitle,
                  name: editedTitle,
                  updatedAt: response.chat.updatedAt,
                }
              : c
          )
        );
        updateChatsOrder();
        setIsEditingTitle(false);
        return { success: true, message: 'Chat title updated successfully' };
      } else {
        throw new Error(response?.error || 'Failed to update chat title');
      }
    } catch (error) {
      console.error('Error in handleTitleSave:', error);
      setError('Failed to update chat title. Please try again.');
      setRetryAction(() => handleTitleSave);
      return { success: false, message: 'Failed to update chat title. Please try again.' };
    }
  }, [user, currentChatId, editedTitle, updateChatsOrder]);

  // Actualizar categoría de un chat
  const updateChatCategory = useCallback(async (chatId, newCategory) => {
   
    if (!user || !user.uid || !chatId || !newCategory) return;
   
    try {
      const response = await updateChatCategoryService(user.uid, chatId, newCategory);
   
   
      if (response && response.success) {
        // Actualizar el estado local de los chats
        setChats((prevChats) => {
          const updatedChats = prevChats.map((c) =>
            c.id === chatId
              ? {
                  ...c,
                  category: newCategory,
                  updatedAt: response.chat.updatedAt,
                }
              : c
          );
          return updatedChats;
        });
   
        updateChatsOrder();
        return { success: true, message: 'Chat category updated successfully' };
      } else {
        throw new Error(response?.error || 'Failed to update chat category');
      }
    } catch (error) {
      console.error('Error en useChat updateChatCategory:', error);
      setError('Failed to update chat category. Please try again.');
      setRetryAction(() => () => updateChatCategory(chatId, newCategory));
      return { success: false, message: 'Failed to update chat category. Please try again.' };
    }
  }, [user, updateChatsOrder]);

  // Eliminar un chat
  const handleDeleteChat = useCallback(async (chatId) => {
    if (!user || !user.uid || !chatId) return;
    try {
      await deleteChat(user.uid, chatId);

      const updatedChatsResponse = await getChats(user.uid, { limit: 20 });
      setChats(updatedChatsResponse.chats);
      setHasNextPage(updatedChatsResponse.hasMore);
      setLastEvaluatedKey(updatedChatsResponse.lastEvaluatedKey);

      if (currentChatId === chatId) {
        setCurrentChatId(null);
        saveCurrentChatId(null);
        setChatTitle('');
        setMessages([]);
        setVisibleMessages([]);
        setChatContext({});
        setIsNewChatView(true);
      }

      delete chatCache.current[chatId];
      updateChatsOrder();
      return { success: true, message: 'Chat deleted successfully' };
    } catch (error) {
      console.error('Error in handleDeleteChat:', error);
      setError('Failed to delete chat. Please try again.');
      setRetryAction(() => () => handleDeleteChat(chatId));
      return { success: false, message: 'Failed to delete chat. Please try again.' };
    }
  }, [
    user,
    currentChatId,
    saveCurrentChatId,
    updateChatsOrder,
  ]);

  // Crear nuevo chat (reset UI)
  const createNewChatHandler = useCallback(() => {
    // Si había un proyecto seleccionado, lo deseleccionamos
    setSelectedProject(null);
    setProjectIdForBackend(null);

    setCurrentChatId(null);
    saveCurrentChatId(null);
    setMessages([]);
    setVisibleMessages([]);
    setChatContext({});
    setChatTitle('');
    setIsNewChatView(true);
  }, [saveCurrentChatId]);

  // Cargar más mensajes (paginación de mensajes)
  const loadMoreMessages = useCallback(() => {
    const currentLength = visibleMessages.length;
    const newMessages = messages.slice(-currentLength - 20, -currentLength);
    setVisibleMessages((prev) => [...newMessages, ...prev]);
  }, [messages, visibleMessages]);

  // Intentar restaurar último chat al cargar
  useEffect(() => {
    const savedChatId = localStorage.getItem('currentChatId');
    if (savedChatId && user && user.uid && isFirstLoad) {
      selectChat(savedChatId);
    }
  }, [user, selectChat, isFirstLoad]);

  // Seleccionar proyecto (y deseleccionar chat)
  const handleSelectProject = useCallback((project) => {
    // Deseleccionamos chat
    setCurrentChatId(null);
    saveCurrentChatId(null);
    // Seleccionamos el proyecto
    setSelectedProject(project);
    setProjectIdForBackend(project ? project.id : null);
  }, [saveCurrentChatId]);

  // Manejo de selección de un chat de proyecto
  const handleProjectChatSelect = useCallback(
    async (chatId, isNew = false, newView = null) => {
      // MODIFICADO: Si chatId es null, es un nuevo chat que aún no se ha creado
      if (!chatId) {
        // Resetear a vista de nuevo chat
        setCurrentChatId(null);
        saveCurrentChatId(null);
        setMessages([]);
        setVisibleMessages([]);
        setChatContext({});
        setIsNewChatView(true);

        if (selectedProject) {
          setSelectedProject((prev) => (prev ? { ...prev, currentView: 'chat' } : prev));
          setProjectIdForBackend(selectedProject.id);

          // Guardamos info de proyecto en chatContext para cuando se cree el chat
          setChatContext({
            project: {
              projectId: selectedProject.id,
              category: selectedProject.category,
              name: selectedProject.name
            }
          });
        }
        return;
      }

      // Limpiamos estado inicial
      setCurrentChatId(null);
      saveCurrentChatId(null);
      setMessages([]);
      setVisibleMessages([]);
      setChatContext({});
      if (selectedProject?.id) {
        setProjectIdForBackend(selectedProject.id);
      }
      if (selectedProject) {
        setSelectedProject((prev) => (prev ? { ...prev, currentView: 'chat' } : prev));
      }

      if (isNew) {
        // Chat recién creado
        setCurrentChatId(chatId);
        saveCurrentChatId(chatId);
        setIsNewChatView(false);

        // Cargamos el chat de inmediato
        await selectChat(chatId);

        // Ajustamos la vista del proyecto si hace falta
        if (selectedProject && newView) {
          setSelectedProject((prev) => ({
            ...prev,
            currentView: newView
          }));
        }

        // Guardamos info de proyecto en chatContext, si aplica
        if (selectedProject) {
          setChatContext((prev) => ({
            ...prev,
            project: {
              projectId: selectedProject.id,
              category: selectedProject.category,
              name: selectedProject.name
            }
          }));
        }
      } else {
        // Chat existente
        await selectChat(chatId);
      }
    },
    [selectChat, saveCurrentChatId, selectedProject, setSelectedProject]
  );

  return {
    // Estados principales
    chats,
    setChats,
    currentChatId,
    setCurrentChatId,
    messages,
    setMessages,
    visibleMessages,
    setVisibleMessages,
    chatContext,
    setChatContext,
    chatTitle,
    setChatTitle,
    isEditingTitle,
    setIsEditingTitle,
    editedTitle,
    setEditedTitle,
    isNewChatView,
    setIsNewChatView,
    
    // Estados de carga/error
    isLoading,
    loadingStates,
    error,
    setError,
    retryAction,
    setRetryAction,
    
    // Paginación
    lastEvaluatedKey,
    isNextPageLoading,
    hasNextPage,
    setHasNextPage,
    
    // Proyecto
    projectIdForBackend,
    setProjectIdForBackend,
    
    // Métodos
    saveCurrentChatId,
    updateChatsOrder,
    loadChats,
    selectChat,
    loadChatContext,
    handleTitleEdit,
    handleTitleSave,
    updateChatCategory,
    handleDeleteChat,
    createNewChatHandler,
    loadMoreMessages,
    
    // Categorías y filtros
    selectedCategory,
    setSelectedCategory,
    customCategories,
    setCustomCategories,
    selectedProject,
    setSelectedProject,
    isSearchMode,
    setIsSearchMode,
    totalSearchResults,
    
    // Manejo de proyectos
    handleSelectProject,
    handleProjectChatSelect,
    
    // Estado persistente
    persistentChatState,
    setPersistentChatState,
    
    // Caché
    getCachedChat,
    setCachedChat
  };
}
